import {useCallback, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {SimphonyService} from "crqlar-react-common/dist/services/integrations/simphony/simphony.service";
import {Box, Divider, Stack, Tab} from "@mui/material";
import {TabContext} from "@mui/lab";
import Typography from "@mui/material/Typography";
import {SimphonyConfig} from "crqlar-react-common/dist/models/integrations/simphony/simphony.config";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import SimphonyConfigForm from "./SimphonyConfigForm";
import SimphonyTableMapping from "./SimphonyTableMapping";
import SimphonyTasks from "./SimphonyTasks";
import SimphonyPosTills from "./SimphonyPosTills";
import Logs from "./Logs";
import SimphonyErrorLog, {SIMPHONY_EVENTS_QUERY_KEY} from "./SimphonyErrorLog";
import {SimphonyEventService} from "crqlar-react-common/dist/services/integrations/simphony/simphonyEvent.service";
import {useQuery} from "react-query";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";

const SimphonyConfigDetails = () => {

    useTrackScreenVisit('system simphony config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'tables', 'tills', 'tasks', 'errors', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    // Table bindings live on floor-plan resources, not on the connector document, so they cannot
    // ride along inside the config payload. They still save from the page's own save button: the
    // Tables tab registers a flush here, and the entity's update runs it after the config is saved.
    const saveTableBindings = useRef<() => Promise<void>>()
    const [tablesDirty, setTablesDirty] = useState(false)

    const registerTableSave = useCallback((save: () => Promise<void>) => {
        saveTableBindings.current = save
    }, [])

    const simphonyConfigData = useEntity<SimphonyConfig>({
        get: async () => {
            return await SimphonyService.find()
        },
        create: SimphonyService.create,
        update: async (simphonyConfig) => {
            const saved = await SimphonyService.update(simphonyConfig)
            // After the config, so a revenue center mapped in the same edit exists before its
            // tables are bound against it.
            await saveTableBindings.current?.()
            setTablesDirty(false)
            return saved
        },
        delete: (id) => SimphonyService.delete({id: [id]}),
        isValid: (simphonyConfig) => {
            return !!simphonyConfig.name
        },
        cleanupBeforeSave: (simphonyConfig) => {
            return {
                ...simphonyConfig,
                name: simphonyConfig.name?.trim(),
                credentials: simphonyConfig.credentials && {
                    ...simphonyConfig.credentials,
                    // Kept as typed: orgShortName is also the sign-in orgname, which is
                    // case-sensitive in practice (the verified sign-in used OPN).
                    orgShortName: simphonyConfig.credentials.orgShortName?.trim(),
                    locRef: simphonyConfig.credentials.locRef?.trim(),
                },
                dataProcessingRules: simphonyConfig.dataProcessingRules && {
                    ...simphonyConfig.dataProcessingRules,
                    // A half-finished row (a revenue center with no sector, or a sector with no
                    // number) would silently widen or narrow the sync scope. Drop them.
                    rvcRefToSectorId: Object.fromEntries(
                        Object.entries(simphonyConfig.dataProcessingRules.rvcRefToSectorId || {})
                            .filter(([rvcRef, sectorId]) => !!rvcRef?.trim() && !!sectorId)
                    )
                }
            }
        }
    })

    const simphonyConfig = simphonyConfigData.item

    // Open problems, counted on the tab so they are seen without opening it.
    const openErrorsQuery = useQuery(
        [SIMPHONY_EVENTS_QUERY_KEY, simphonyConfig.id, 'openCount'],
        () => SimphonyEventService.getAll({configId: simphonyConfig.id!, openOnly: true, limit: {offset: 0, limit: 1}}),
        {enabled: !!simphonyConfig.id, refetchInterval: 60_000}
    )
    const openErrors = openErrorsQuery.data?.totalElements ?? 0

    const handleCreate = async () => {
        simphonyConfigData.startCreate({
            pollerConfig: {
                // Two minutes, deliberately. Simphony lists only open checks, so a check opened and
                // paid between two quick syncs is never seen — the period is a correctness setting,
                // not a performance one. See SimphonyCheckSyncService in the API.
                quickSync: {
                    enabled: true,
                    syncPeriodMinutes: 2,
                    staleTimeoutMinutes: 10
                },
                daySync: {
                    enabled: true,
                    staleTimeoutMinutes: 30
                }
            },
            dataProcessingRules: {
                rvcRefToSectorId: {},
                excludedRvcRefs: [],
                pmsBilledRvcRefs: [],
                autoCreateServiceIfNotFound: true
            }
        } as SimphonyConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={simphonyConfig.name}
            data={simphonyConfigData}
            back={() => navigate('/system')}
            // Binding a table leaves the config document untouched, so without this the save
            // button stays greyed out over genuinely unsaved work.
            forceEnableSave={tablesDirty}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={simphonyConfigData.saving}
                onDelete={() => simphonyConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {simphonyConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    simphonyConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!simphonyConfig.id &&
                    <TabContext value={activeTab}>
                        <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                            <Tab label={t('Configuration')} value={'config'}/>
                            <Tab label={t('Tables')} value={'tables'}/>
                            <Tab label={t('POS tills')} value={'tills'}/>
                            <Tab label={t('Tasks')} value={'tasks'}/>
                            <Tab
                                label={openErrors > 0 ? `${t('Error log')} (${openErrors})` : t('Error log')}
                                value={'errors'}
                            />
                            <Tab label={t('Stats and Logs')} value={'logs'}/>
                        </ScrollableTabList>
                    </TabContext>
                }

                {
                    !simphonyConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <SimphonyConfigForm simphonyConfigData={simphonyConfigData}/>

                            {
                                !!simphonyConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'tables' && (
                        <SimphonyTableMapping
                            configId={simphonyConfig.id}
                            onDirtyChange={setTablesDirty}
                            registerSave={registerTableSave}
                        />
                    )
                }

                {
                    activeTab === 'tills' && !!simphonyConfig.id && (
                        <SimphonyPosTills/>
                    )
                }

                {
                    activeTab === 'tasks' && (
                        <SimphonyTasks configData={simphonyConfigData}/>
                    )
                }

                {
                    activeTab === 'errors' && !!simphonyConfig.id && (
                        <SimphonyErrorLog configId={simphonyConfig.id}/>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs configData={simphonyConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {simphonyConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default SimphonyConfigDetails;
